import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  Chip,
  Skeleton,
  Pagination,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CircleIcon from '@mui/icons-material/Circle';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../service/notificationService';

const NotificationsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [notifications, setNotifications] = useState({ unread: [], read: [] });
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAlert, setShowAlert] = useState('');
  const navigate = useNavigate();

  const PRIMARY_COLOR = '#7C0BCC';

  const fetchNotifications = useCallback(async (pageNum = 1, limit = 20) => {
    setLoading(true);
    try {
      const response = await notificationService.getNotifications(pageNum, limit);
      const allNotifications = response.data?.data || [];
      const unread = allNotifications.filter(n => !n.is_read);
      const read = allNotifications.filter(n => n.is_read);

      setNotifications({ unread, read });
      setUnreadCount(response.unread_count || 0);
      setTotalPages(response.data?.last_page || 1);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setShowAlert('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications(page);
  }, [page, fetchNotifications]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    setActionLoading(true);
    try {
      await notificationService.readAll();
      setShowAlert('All notifications marked as read');
      await fetchNotifications(page);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      setShowAlert('Failed to mark all as read');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearAll = async () => {
    setActionLoading(true);
    try {
      await notificationService.clearAll();
      setShowAlert('All notifications cleared');
      setNotifications({ unread: [], read: [] });
      setUnreadCount(0);
      setPage(1);
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
      setShowAlert('Failed to clear notifications');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    setActionLoading(true);
    try {
      await notificationService.markAsRead(notificationId);
      await fetchNotifications(page);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60 / 60 / 24);

    if (diff === 0) return 'Today';
    if (diff === 1) return '1d ago';
    if (diff < 30) return `${diff}d ago`;
    return date.toLocaleDateString();
  };

  // New: Handle click on notification
  const handleNotificationClick = async (notif) => {
    // If unread, mark as read first
    if (!notif.is_read) {
      await handleMarkAsRead(notif.id);
    }

    // Navigate to action_url if exists
    if (notif.action_url) {
      try {
        const url = new URL(notif.action_url);
        // Extract pathname + search (e.g., /view-order/57)
        navigate(url.pathname + url.search);
      } catch (error) {
        console.error('Invalid action_url:', notif.action_url);
        // Fallback: open in new tab or same window
        window.location.href = notif.action_url;
      }
    }
  };

  const renderNotificationItem = (notif, index) => (
    <ListItem
      key={notif.id || index}
      sx={{
        padding: { xs: '12px 16px', sm: '16px' },
        minHeight: { xs: '64px', sm: '72px' },
        borderBottom: '1px solid',
        borderColor: 'divider',
        alignItems: 'flex-start',
        '&:hover': { backgroundColor: 'action.hover' },
        cursor: 'pointer',
      }}
      onClick={() => handleNotificationClick(notif)} // Updated click handler
    >
      <ListItemAvatar sx={{ minWidth: { xs: '48px', sm: '52px' } }}>
        <Avatar
          sx={{
            bgcolor: PRIMARY_COLOR,
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 }
          }}
        >
          {notif.avatar || notif.initials || notif.branch?.name?.[0] || 'N'}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
            <Typography
              variant="body1"
              sx={{
                flex: 1,
                fontWeight: notif.is_read ? 400 : 500,
                color: notif.is_read ? 'text.secondary' : 'text.primary',
                fontSize: { xs: '0.7rem', sm: '0.8rem', md: '.9rem', lg: '.9rem' },
                lineHeight: 1.2
              }}
            >
              {notif.message || notif.title}
            </Typography>
            {!notif.is_read && (
              <CircleIcon sx={{ color: PRIMARY_COLOR, fontSize: { xs: 10, sm: 10, md: 10, lg: 10 }, ml: 1, mt: 0.5 }} />
            )}
          </Box>
        }
        secondary={
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {formatTime(notif.created_at)} - {notif.branch?.name || ''}
          </Typography>
        }
      />
    </ListItem>
  );

  const currentNotifications = tabValue === 0 ? notifications.unread : notifications.read;

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default'
    }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          zIndex: 1200,
          borderBottom: 1,
          borderColor: 'divider',
          top: 0
        }}
      >
        <Toolbar sx={{
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 1.5, sm: 2 }
        }}>
          <IconButton
            edge="start"
            onClick={() => navigate(-1)}
            sx={{ mr: { xs: 0.5, sm: 1 }, color: 'text.primary' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            fontWeight={600}
            flexGrow={1}
            sx={{
              fontSize: { xs: '.9rem', md: '1rem' },
              whiteSpace: 'nowrap'
            }}
          >
            Notifications
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} unread`}
                size="small"
                color="error"
                sx={{
                  ml: { xs: 0.5, sm: 1 },
                  height: { xs: 18, sm: 22 },
                  fontSize: '0.60rem',
                  minWidth: 'auto',
                  px: 0.75
                }}
              />
            )}
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: { xs: 0.5, sm: 1 },
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'nowrap',
            minWidth: 0
          }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: PRIMARY_COLOR,
                '&:hover': { backgroundColor: `${PRIMARY_COLOR}CC` },
                color: 'white',
                boxShadow: 'none',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                px: { xs: 1.2, sm: 1.5, md: 2 },
                py: { xs: 0.75, sm: 0.8 },
                minWidth: { xs: 'auto', sm: 120 },
                flexShrink: 1
              }}
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0 || actionLoading}
              size="small"
            >
              Read All
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleClearAll}
              disabled={actionLoading || (notifications.unread.length === 0 && notifications.read.length === 0)}
              size="small"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                px: { xs: 1.2, sm: 1.5, md: 2 },
                py: { xs: 0.75, sm: 0.8 },
                minWidth: { xs: 'auto', sm: 100 },
                ml: { xs: 0.5, sm: 1 },
                flexShrink: 1
              }}
              startIcon={<DeleteIcon fontSize="small" />}
            >
              Clear All
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {showAlert && (
        <Alert
          severity="info"
          onClose={() => setShowAlert('')}
          sx={{
            mx: { xs: 1.5, sm: 2 },
            mt: 1
          }}
        >
          {showAlert}
        </Alert>
      )}

      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0
      }}>
        <Box sx={{
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0
        }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: { xs: 48, sm: 56 },
              '& .MuiTabs-flexContainer': {
                gap: 1,
                px: { xs: 1, sm: 0 }
              }
            }}
            TabIndicatorProps={{
              style: {
                backgroundColor: PRIMARY_COLOR,
                height: 3
              }
            }}
          >
            <Tab
              label={`New & Unread (${unreadCount})`}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                minHeight: { xs: 48, sm: 56 },
                py: 0,
                px: 1,
                '&.Mui-selected': { color: PRIMARY_COLOR },
                '&:hover': { color: `${PRIMARY_COLOR}CC` }
              }}
            />
            <Tab
              label={`Archive & Read (${notifications.read.length})`}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                minHeight: { xs: 48, sm: 56 },
                py: 0,
                px: 1,
                '&.Mui-selected': { color: PRIMARY_COLOR },
                '&:hover': { color: `${PRIMARY_COLOR}CC` }
              }}
            />
          </Tabs>
        </Box>

        <Box sx={{
          flex: 1,
          overflow: 'auto',
          p: { xs: 1.5, sm: 2, md: 3 },
          WebkitOverflowScrolling: 'touch'
        }}>
          {loading ? (
            <List sx={{ p: 0 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <ListItem key={i} sx={{ py: { xs: 2, sm: 3 } }}>
                  <ListItemAvatar>
                    <Skeleton variant="circular" width={40} height={40} />
                  </ListItemAvatar>
                  <Box sx={{ flex: 1, ml: 2 }}>
                    <Skeleton width="60%" height={20} sx={{ mb: 1 }} />
                    <Skeleton width="40%" height={14} />
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <>
              <List sx={{ p: 0 }}>
                {currentNotifications.map((notif, index) => renderNotificationItem(notif, index))}
                {currentNotifications.length === 0 && (
                  <Box sx={{
                    textAlign: 'center',
                    p: { xs: 4, sm: 8 },
                    color: 'text.secondary',
                    mt: { xs: 2, sm: 4 }
                  }}>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                      {tabValue === 0 ? 'No new notifications' : 'No archived notifications'}
                    </Typography>
                    <Typography variant="body2">
                      {tabValue === 0 ? 'Check back later for updates' : 'All notifications are up to date'}
                    </Typography>
                  </Box>
                )}
              </List>

              {totalPages > 1 && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: { xs: 2, sm: 3 },
                  mt: 2
                }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size={window.innerWidth < 600 ? "small" : "large"}
                    disabled={actionLoading}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        minWidth: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NotificationsPage;